import { apiService } from './api';

type AnyObject = Record<string, any>;

const unwrapList = <T = any>(value: any): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (Array.isArray(value?.results)) return value.results as T[];
  if (Array.isArray(value?.sessions)) return value.sessions as T[];
  if (Array.isArray(value?.prompts)) return value.prompts as T[];
  return [];
};

const unwrapObject = <T = any>(value: any): T => {
  if (value?.data && typeof value.data === 'object') return value.data as T;
  return value as T;
};

export const nodeEditorService = {
  async getSessions() {
    const res = await apiService.get<any>('/node-editor/sessions/');
    return unwrapList(res);
  },
  async getMySessions() {
    const res = await apiService.get<any>('/node-editor/sessions/my_sessions/');
    return unwrapList(res);
  },
  async getPublishedSessions() {
    const res = await apiService.get<any>('/node-editor/sessions/published/');
    return unwrapList(res);
  },
  async getSession(id: string) {
    return apiService.get<any>(`/node-editor/sessions/${id}/`);
  },
  async createSession(payload: AnyObject) {
    const sanitizedPayload: AnyObject = { ...payload };
    if (!sanitizedPayload.node_id) {
      delete sanitizedPayload.node_id;
    }
    if (!sanitizedPayload.node_display_name && sanitizedPayload.node_name) {
      sanitizedPayload.node_display_name = sanitizedPayload.node_name;
    }
    return apiService.post<any>('/node-editor/sessions/', sanitizedPayload);
  },
  async saveSession(id: string, payload: AnyObject) {
    return apiService.post<any>(`/node-editor/sessions/${id}/save/`, payload);
  },
  async deleteSession(id: string) {
    return apiService.delete<any>(`/node-editor/sessions/${id}/`);
  },
  async generateFrontend(id: string, requirements?: string) {
    return apiService.post<any>(`/node-editor/sessions/${id}/generate_frontend/`, { requirements });
  },
  async generateBackend(id: string, requirements?: string) {
    return apiService.post<any>(`/node-editor/sessions/${id}/generate_backend/`, { requirements });
  },
  async autoLayout(id: string) {
    return apiService.post<any>(`/node-editor/sessions/${id}/auto_layout/`);
  },
  async generateReact(id: string) {
    return apiService.post<any>(`/node-editor/sessions/${id}/generate_react/`);
  },
  async chat(id: string, message: string) {
    return apiService.post<any>(`/node-editor/sessions/${id}/chat/`, { message });
  },
  async publishSession(id: string, payload: AnyObject) {
    return apiService.post<any>(`/node-editor/sessions/${id}/publish/`, payload);
  },

  async getLLMConfigs() {
    const res = await apiService.get<any>('/node-editor/llm-configs/');
    return unwrapList(res);
  },
  async createLLMConfig(payload: AnyObject) {
    return apiService.post<any>('/node-editor/llm-configs/', payload);
  },
  async updateLLMConfig(id: string, payload: AnyObject) {
    return apiService.patch<any>(`/node-editor/llm-configs/${id}/`, payload);
  },
  async deleteLLMConfig(id: string) {
    return apiService.delete<any>(`/node-editor/llm-configs/${id}/`);
  },

  async getPrompts() {
    const res = await apiService.get<any>('/node-editor/prompts/');
    return unwrapList(res);
  },
  async getQuickPrompts() {
    const res = await apiService.get<any>('/node-editor/prompts/quick_links/');
    return unwrapList(res);
  },
  async createPrompt(payload: AnyObject) {
    return apiService.post<any>('/node-editor/prompts/', payload);
  },
  async updatePrompt(id: string, payload: AnyObject) {
    return apiService.patch<any>(`/node-editor/prompts/${id}/`, payload);
  },
  async deletePrompt(id: string) {
    return apiService.delete<any>(`/node-editor/prompts/${id}/`);
  },

  async getMyPermissions() {
    const res = await apiService.get<any>('/node-editor/permissions/my_permissions/');
    return unwrapObject(res);
  },
};

// Backward-compat exports used in existing tests/components.
export const sessionApi = {
  getSession: nodeEditorService.getSession,
  createSession: nodeEditorService.createSession,
  save: (id: string) => nodeEditorService.saveSession(id, {}),
};

